import Decimal from 'decimal.js';
import { BalanceModification } from './BalanceModification';
import { Currency } from '../../utils/Currency';
import { Calendar } from '../Calendar';
import { DateUtil } from '../../utils/DateUtil';
/**
 * Represents one entry/row in an amortization schedule.
 */
export class AmortizationEntry {
    //
    // ================= PRIVATE BACKING FIELDS =================
    //
    _term;
    _periodStartDate;
    _periodEndDate;
    _prebillDaysConfiguration;
    _billDueDaysAfterPeriodEndConfiguration;
    _billablePeriod;
    _periodBillOpenDate;
    _periodBillDueDate;
    _periodInterestRate;
    _principal;
    _dueInterestForTerm;
    _accruedInterestForPeriod;
    _billedInterestForTerm;
    _billedDeferredInterest;
    _unbilledTotalDeferredInterest;
    _fees;
    _billedDeferredFees;
    _unbilledTotalDeferredFees;
    _totalPayment;
    _endBalance;
    _startBalance;
    _balanceModificationAmount;
    _balanceModification;
    _perDiem;
    _daysInPeriod;
    _interestRoundingError;
    _unbilledInterestDueToRounding;
    _calendar;
    _metadata = {};
    _billingModel;
    // DSI fields
    actualDSIPrincipal;
    actualDSIInterest;
    actualDSIFees;
    dsiInterestSavings;
    dsiInterestPenalty;
    usageDetails = [];
    // DSI actual balance tracking
    actualDSIStartBalance;
    actualDSIEndBalance;
    // DSI interest duration tracking
    dsiInterestDays;
    dsiPreviousPaymentDate;
    // Re-amortized balance fields
    reAmortizedStartBalance;
    reAmortizedEndBalance;
    // Re-amortized payment breakdown
    reAmortizedInterest;
    reAmortizedPrincipal;
    reAmortizedFees;
    reAmortizedTotalPayment;
    // Re-amortized DSI tracking
    reAmortizedDsiInterestDays;
    reAmortizedPerDiem;
    // Status flags
    isCurrentActiveTerm;
    isDelinquent;
    lastPaymentDate;
    //
    // =========== PUBLIC "js*" PROPERTIES FOR UI BINDING ===========
    //
    jsTerm;
    jsPeriodStartDate; // store ISO or "YYYY-MM-DD"
    jsPeriodEndDate;
    jsPrebillDaysConfiguration;
    jsBillDueDaysAfterPeriodEndConfiguration;
    jsBillablePeriod;
    jsPeriodBillOpenDate;
    jsPeriodBillDueDate;
    jsPeriodInterestRate; // e.g. "0.06" or "6" or "0.06..."
    jsPrincipal;
    jsDueInterestForTerm;
    jsAccruedInterestForPeriod;
    jsBilledInterestForTerm;
    jsBilledDeferredInterest;
    jsUnbilledTotalDeferredInterest;
    jsFees;
    jsBilledDeferredFees;
    jsUnbilledTotalDeferredFees;
    jsTotalPayment;
    jsEndBalance;
    jsStartBalance;
    jsBalanceModificationAmount;
    // Could define `jsBalanceModification` if needed.
    jsPerDiem;
    jsDaysInPeriod;
    jsInterestRoundingError;
    jsUnbilledInterestDueToRounding;
    jsMetadata;
    //
    // ===================== CONSTRUCTOR =====================
    //
    constructor(params) {
        this.term = params.term;
        this.periodStartDate = params.periodStartDate;
        this.periodEndDate = params.periodEndDate;
        this.prebillDaysConfiguration = params.prebillDaysConfiguration;
        this.billDueDaysAfterPeriodEndConfiguration = params.billDueDaysAfterPeriodEndConfiguration;
        this.billablePeriod = params.billablePeriod;
        this.periodBillOpenDate = params.periodBillOpenDate;
        this.periodBillDueDate = params.periodBillDueDate;
        this.periodInterestRate = params.periodInterestRate;
        this.principal = params.principal;
        this.dueInterestForTerm = params.dueInterestForTerm;
        this.accruedInterestForPeriod = params.accruedInterestForPeriod;
        this.billedInterestForTerm = params.billedInterestForTerm;
        this.billedDeferredInterest = params.billedDeferredInterest;
        this.unbilledTotalDeferredInterest = params.unbilledTotalDeferredInterest;
        this.fees = params.fees;
        this.billedDeferredFees = params.billedDeferredFees;
        this.unbilledTotalDeferredFees = params.unbilledTotalDeferredFees;
        this.totalPayment = params.totalPayment;
        this.endBalance = params.endBalance;
        this.startBalance = params.startBalance;
        this.balanceModificationAmount = params.balanceModificationAmount;
        if (params.balanceModification) {
            // If it's a real object, parse it:
            if (params.balanceModification instanceof BalanceModification) {
                this._balanceModification = params.balanceModification;
            }
            else {
                this._balanceModification = BalanceModification.fromJSON(params.balanceModification);
            }
        }
        this.perDiem = params.perDiem;
        this.daysInPeriod = params.daysInPeriod;
        this.interestRoundingError = params.interestRoundingError;
        this.unbilledInterestDueToRounding = params.unbilledInterestDueToRounding;
        this.calendar = params.calendar;
        this.metadata = params.metadata ?? {};
        this.billingModel = params.billingModel;
        if (params.actualDSIPrincipal)
            this.actualDSIPrincipal = params.actualDSIPrincipal;
        if (params.actualDSIInterest)
            this.actualDSIInterest = params.actualDSIInterest;
        if (params.actualDSIFees)
            this.actualDSIFees = params.actualDSIFees;
        if (params.dsiInterestSavings)
            this.dsiInterestSavings = params.dsiInterestSavings;
        if (params.dsiInterestPenalty)
            this.dsiInterestPenalty = params.dsiInterestPenalty;
        if (params.usageDetails)
            this.usageDetails = params.usageDetails;
        if (params.actualDSIStartBalance !== undefined) {
            this.actualDSIStartBalance = Currency.of(params.actualDSIStartBalance);
        }
        if (params.actualDSIEndBalance !== undefined) {
            this.actualDSIEndBalance = Currency.of(params.actualDSIEndBalance);
        }
        if (params.dsiInterestDays !== undefined) {
            this.dsiInterestDays = params.dsiInterestDays;
        }
        if (params.dsiPreviousPaymentDate) {
            this.dsiPreviousPaymentDate = params.dsiPreviousPaymentDate;
        }
        // Initialize re-amortized fields
        if (params.reAmortizedStartBalance !== undefined) {
            this.reAmortizedStartBalance = Currency.of(params.reAmortizedStartBalance);
        }
        if (params.reAmortizedEndBalance !== undefined) {
            this.reAmortizedEndBalance = Currency.of(params.reAmortizedEndBalance);
        }
        if (params.reAmortizedInterest !== undefined) {
            this.reAmortizedInterest = Currency.of(params.reAmortizedInterest);
        }
        if (params.reAmortizedPrincipal !== undefined) {
            this.reAmortizedPrincipal = Currency.of(params.reAmortizedPrincipal);
        }
        if (params.reAmortizedFees !== undefined) {
            this.reAmortizedFees = Currency.of(params.reAmortizedFees);
        }
        if (params.reAmortizedTotalPayment !== undefined) {
            this.reAmortizedTotalPayment = Currency.of(params.reAmortizedTotalPayment);
        }
        if (params.reAmortizedDsiInterestDays !== undefined) {
            this.reAmortizedDsiInterestDays = params.reAmortizedDsiInterestDays;
        }
        if (params.reAmortizedPerDiem !== undefined) {
            this.reAmortizedPerDiem = Currency.of(params.reAmortizedPerDiem);
        }
        if (params.isCurrentActiveTerm !== undefined) {
            this.isCurrentActiveTerm = params.isCurrentActiveTerm;
        }
        if (params.isDelinquent !== undefined) {
            this.isDelinquent = params.isDelinquent;
        }
        if (params.lastPaymentDate) {
            this.lastPaymentDate = params.lastPaymentDate;
        }
        // Finally, push model data → jsX for the UI
        this.updateJsValues();
    }
    //
    // ===================== GETTERS & SETTERS =====================
    //
    get calendar() {
        return this._calendar;
    }
    set calendar(value) {
        if (!(value instanceof Calendar)) {
            value = new Calendar(value);
        }
        this._calendar = value;
    }
    get term() {
        return this._term;
    }
    set term(value) {
        if (typeof value !== 'number') {
            throw new Error(`term must be a number. Received: ${typeof value}`);
        }
        this._term = value;
    }
    get periodStartDate() {
        return this._periodStartDate;
    }
    set periodStartDate(raw) {
        this._periodStartDate = DateUtil.normalizeDate(raw);
    }
    get periodEndDate() {
        return this._periodEndDate;
    }
    set periodEndDate(raw) {
        this._periodEndDate = DateUtil.normalizeDate(raw);
    }
    get prebillDaysConfiguration() {
        return this._prebillDaysConfiguration;
    }
    set prebillDaysConfiguration(val) {
        this._prebillDaysConfiguration = this.parseNumber(val, 'prebillDaysConfiguration');
    }
    get billDueDaysAfterPeriodEndConfiguration() {
        return this._billDueDaysAfterPeriodEndConfiguration;
    }
    set billDueDaysAfterPeriodEndConfiguration(val) {
        this._billDueDaysAfterPeriodEndConfiguration = this.parseNumber(val, 'billDueDaysAfterPeriodEndConfiguration');
    }
    get billablePeriod() {
        return this._billablePeriod;
    }
    set billablePeriod(raw) {
        if (typeof raw !== 'boolean') {
            throw new Error(`billablePeriod must be boolean, got: ${raw}`);
        }
        this._billablePeriod = raw;
    }
    get periodBillOpenDate() {
        return this._periodBillOpenDate;
    }
    set periodBillOpenDate(raw) {
        this._periodBillOpenDate = DateUtil.normalizeDate(raw);
    }
    get periodBillDueDate() {
        return this._periodBillDueDate;
    }
    set periodBillDueDate(raw) {
        this._periodBillDueDate = DateUtil.normalizeDate(raw);
    }
    get periodInterestRate() {
        return this._periodInterestRate;
    }
    set periodInterestRate(raw) {
        if (raw instanceof Decimal) {
            this._periodInterestRate = raw;
        }
        else if (typeof raw === 'number' || typeof raw === 'string') {
            this._periodInterestRate = new Decimal(raw);
        }
        else {
            throw new Error(`periodInterestRate must be Decimal|number|string, got: ${typeof raw}`);
        }
    }
    get principal() {
        return this._principal;
    }
    set principal(raw) {
        this._principal = this.parseCurrency(raw, 'principal');
    }
    get dueInterestForTerm() {
        return this._dueInterestForTerm;
    }
    set dueInterestForTerm(raw) {
        this._dueInterestForTerm = this.parseCurrency(raw, 'dueInterestForTerm');
    }
    get accruedInterestForPeriod() {
        return this._accruedInterestForPeriod;
    }
    set accruedInterestForPeriod(raw) {
        this._accruedInterestForPeriod = this.parseCurrency(raw, 'accruedInterestForPeriod');
    }
    get billedInterestForTerm() {
        return this._billedInterestForTerm;
    }
    set billedInterestForTerm(raw) {
        this._billedInterestForTerm = this.parseCurrency(raw, 'billedInterestForTerm');
    }
    get billedDeferredInterest() {
        return this._billedDeferredInterest;
    }
    set billedDeferredInterest(raw) {
        this._billedDeferredInterest = this.parseCurrency(raw, 'billedDeferredInterest');
    }
    get unbilledTotalDeferredInterest() {
        return this._unbilledTotalDeferredInterest;
    }
    set unbilledTotalDeferredInterest(raw) {
        this._unbilledTotalDeferredInterest = this.parseCurrency(raw, 'unbilledTotalDeferredInterest');
    }
    get fees() {
        return this._fees;
    }
    set fees(raw) {
        this._fees = this.parseCurrency(raw, 'fees');
    }
    get billedDeferredFees() {
        return this._billedDeferredFees;
    }
    set billedDeferredFees(raw) {
        this._billedDeferredFees = this.parseCurrency(raw, 'billedDeferredFees');
    }
    get unbilledTotalDeferredFees() {
        return this._unbilledTotalDeferredFees;
    }
    set unbilledTotalDeferredFees(raw) {
        this._unbilledTotalDeferredFees = this.parseCurrency(raw, 'unbilledTotalDeferredFees');
    }
    get totalPayment() {
        return this._totalPayment;
    }
    set totalPayment(raw) {
        this._totalPayment = this.parseCurrency(raw, 'totalPayment');
    }
    get endBalance() {
        return this._endBalance;
    }
    set endBalance(raw) {
        this._endBalance = this.parseCurrency(raw, 'endBalance');
    }
    get startBalance() {
        return this._startBalance;
    }
    set startBalance(raw) {
        this._startBalance = this.parseCurrency(raw, 'startBalance');
    }
    get balanceModificationAmount() {
        return this._balanceModificationAmount;
    }
    set balanceModificationAmount(raw) {
        this._balanceModificationAmount = this.parseCurrency(raw, 'balanceModificationAmount');
    }
    get balanceModification() {
        return this._balanceModification;
    }
    set balanceModification(raw) {
        if (raw instanceof BalanceModification) {
            this._balanceModification = raw;
        }
        else if (typeof raw === 'object' && raw !== null) {
            this._balanceModification = BalanceModification.fromJSON(raw);
        }
        else if (raw === undefined) {
            this._balanceModification = undefined;
        }
        else {
            throw new Error(`Invalid balanceModification: must be object or BalanceModification`);
        }
    }
    get perDiem() {
        return this._perDiem;
    }
    set perDiem(raw) {
        this._perDiem = this.parseCurrency(raw, 'perDiem');
    }
    get daysInPeriod() {
        return this._daysInPeriod;
    }
    set daysInPeriod(val) {
        this._daysInPeriod = this.parseNumber(val, 'daysInPeriod');
    }
    get interestRoundingError() {
        return this._interestRoundingError;
    }
    set interestRoundingError(raw) {
        this._interestRoundingError = this.parseCurrency(raw, 'interestRoundingError');
    }
    get unbilledInterestDueToRounding() {
        return this._unbilledInterestDueToRounding;
    }
    set unbilledInterestDueToRounding(raw) {
        this._unbilledInterestDueToRounding = this.parseCurrency(raw, 'unbilledInterestDueToRounding');
    }
    get metadata() {
        return this._metadata;
    }
    set metadata(val) {
        // If not provided, default to {}
        if (!val) {
            val = {};
        }
        if (typeof val !== 'object') {
            throw new Error(`metadata must be an object, got: ${typeof val}`);
        }
        this._metadata = val;
    }
    get billingModel() {
        return this._billingModel;
    }
    set billingModel(val) {
        this._billingModel = val;
    }
    //
    // ===================== HELPER PARSE METHODS =====================
    //
    parseNumber(val, fieldName) {
        if (typeof val === 'number')
            return val;
        throw new Error(`${fieldName} must be a number, got: ${val}`);
    }
    parseCurrency(raw, fieldName) {
        if (raw instanceof Currency) {
            return raw;
        }
        else if (typeof raw === 'number') {
            return Currency.of(raw);
        }
        throw new Error(`${fieldName} must be Currency or number, got: ${typeof raw}`);
    }
    //
    // ============= updateJsValues / updateModelValues =============
    //
    /** Push model data → js* fields (for UI binding). */
    updateJsValues() {
        this.jsTerm = this.term;
        this.jsPeriodStartDate = DateUtil.normalizeDateToJsDate(this.periodStartDate);
        this.jsPeriodEndDate = DateUtil.normalizeDateToJsDate(this.periodEndDate);
        this.jsPrebillDaysConfiguration = this.prebillDaysConfiguration;
        this.jsBillDueDaysAfterPeriodEndConfiguration = this.billDueDaysAfterPeriodEndConfiguration;
        this.jsBillablePeriod = this.billablePeriod;
        this.jsPeriodBillOpenDate = DateUtil.normalizeDateToJsDate(this.periodBillOpenDate);
        this.jsPeriodBillDueDate = DateUtil.normalizeDateToJsDate(this.periodBillDueDate);
        this.jsPeriodInterestRate = this.periodInterestRate.toString();
        this.jsPrincipal = this.principal.toNumber();
        this.jsDueInterestForTerm = this.dueInterestForTerm.toNumber();
        this.jsAccruedInterestForPeriod = this.accruedInterestForPeriod.toNumber();
        this.jsBilledInterestForTerm = this.billedInterestForTerm.toNumber();
        this.jsBilledDeferredInterest = this.billedDeferredInterest.toNumber();
        this.jsUnbilledTotalDeferredInterest = this.unbilledTotalDeferredInterest.toNumber();
        this.jsFees = this.fees.toNumber();
        this.jsBilledDeferredFees = this.billedDeferredFees.toNumber();
        this.jsUnbilledTotalDeferredFees = this.unbilledTotalDeferredFees.toNumber();
        this.jsTotalPayment = this.totalPayment.toNumber();
        this.jsEndBalance = this.endBalance.toNumber();
        this.jsStartBalance = this.startBalance.toNumber();
        this.jsBalanceModificationAmount = this.balanceModificationAmount.toNumber();
        // If you want: this.jsBalanceModification = ???
        this.jsPerDiem = this.perDiem.toNumber();
        this.jsDaysInPeriod = this.daysInPeriod;
        this.jsInterestRoundingError = this.interestRoundingError.toNumber();
        this.jsUnbilledInterestDueToRounding = this.unbilledInterestDueToRounding.toNumber();
        this.jsMetadata = this.metadata;
    }
    /** Push js* (UI-edited) fields → real model fields. */
    updateModelValues() {
        this.term = this.jsTerm;
        this.periodStartDate = this.jsPeriodStartDate;
        this.periodEndDate = this.jsPeriodEndDate;
        this.prebillDaysConfiguration = this.jsPrebillDaysConfiguration;
        this.billDueDaysAfterPeriodEndConfiguration = this.jsBillDueDaysAfterPeriodEndConfiguration;
        this.billablePeriod = this.jsBillablePeriod;
        this.periodBillOpenDate = this.jsPeriodBillOpenDate;
        this.periodBillDueDate = this.jsPeriodBillDueDate;
        this.periodInterestRate = this.jsPeriodInterestRate;
        this.principal = this.jsPrincipal;
        this.dueInterestForTerm = this.jsDueInterestForTerm;
        this.accruedInterestForPeriod = this.jsAccruedInterestForPeriod;
        this.billedInterestForTerm = this.jsBilledInterestForTerm;
        this.billedDeferredInterest = this.jsBilledDeferredInterest;
        this.unbilledTotalDeferredInterest = this.jsUnbilledTotalDeferredInterest;
        this.fees = this.jsFees;
        this.billedDeferredFees = this.jsBilledDeferredFees;
        this.unbilledTotalDeferredFees = this.jsUnbilledTotalDeferredFees;
        this.totalPayment = this.jsTotalPayment;
        this.endBalance = this.jsEndBalance;
        this.startBalance = this.jsStartBalance;
        this.balanceModificationAmount = this.jsBalanceModificationAmount;
        // If you want to handle "jsBalanceModification", do it here.
        this.perDiem = this.jsPerDiem;
        this.daysInPeriod = this.jsDaysInPeriod;
        this.interestRoundingError = this.jsInterestRoundingError;
        this.unbilledInterestDueToRounding = this.jsUnbilledInterestDueToRounding;
        this.metadata = this.jsMetadata;
    }
    //
    // ============= toJSON() / fromJSON() =============
    //
    toJSON() {
        const base = {
            term: this.term,
            periodStartDate: this.periodStartDate.toString(),
            periodEndDate: this.periodEndDate.toString(),
            prebillDaysConfiguration: this.prebillDaysConfiguration,
            billDueDaysAfterPeriodEndConfiguration: this.billDueDaysAfterPeriodEndConfiguration,
            billablePeriod: this.billablePeriod,
            periodBillOpenDate: this.periodBillOpenDate.toString(),
            periodBillDueDate: this.periodBillDueDate.toString(),
            periodInterestRate: this.periodInterestRate.toString(),
            principal: this.principal.toNumber(),
            dueInterestForTerm: this.dueInterestForTerm.toNumber(),
            accruedInterestForPeriod: this.accruedInterestForPeriod.toNumber(),
            billedInterestForTerm: this.billedInterestForTerm.toNumber(),
            billedDeferredInterest: this.billedDeferredInterest.toNumber(),
            unbilledTotalDeferredInterest: this.unbilledTotalDeferredInterest.toNumber(),
            fees: this.fees.toNumber(),
            billedDeferredFees: this.billedDeferredFees.toNumber(),
            unbilledTotalDeferredFees: this.unbilledTotalDeferredFees.toNumber(),
            totalPayment: this.totalPayment.toNumber(),
            endBalance: this.endBalance.toNumber(),
            startBalance: this.startBalance.toNumber(),
            balanceModificationAmount: this.balanceModificationAmount.toNumber(),
            balanceModification: this.balanceModification ? this.balanceModification.json : undefined,
            perDiem: this.perDiem.toNumber(),
            daysInPeriod: this.daysInPeriod,
            interestRoundingError: this.interestRoundingError.toNumber(),
            unbilledInterestDueToRounding: this.unbilledInterestDueToRounding.toNumber(),
            calendar: this.calendar.calendarType,
            metadata: this.metadata,
            billingModel: this.billingModel,
            actualDSIPrincipal: this.actualDSIPrincipal?.toNumber?.() ?? undefined,
            actualDSIInterest: this.actualDSIInterest?.toNumber?.() ?? undefined,
            actualDSIFees: this.actualDSIFees?.toNumber?.() ?? undefined,
            dsiInterestSavings: this.dsiInterestSavings,
            dsiInterestPenalty: this.dsiInterestPenalty,
            usageDetails: this.usageDetails,
            actualDSIStartBalance: this.actualDSIStartBalance?.toNumber?.() ?? undefined,
            actualDSIEndBalance: this.actualDSIEndBalance?.toNumber?.() ?? undefined,
            dsiInterestDays: this.dsiInterestDays,
            dsiPreviousPaymentDate: this.dsiPreviousPaymentDate?.toString?.() ?? undefined,
            // Re-amortized fields
            reAmortizedStartBalance: this.reAmortizedStartBalance?.toNumber?.() ?? undefined,
            reAmortizedEndBalance: this.reAmortizedEndBalance?.toNumber?.() ?? undefined,
            reAmortizedInterest: this.reAmortizedInterest?.toNumber?.() ?? undefined,
            reAmortizedPrincipal: this.reAmortizedPrincipal?.toNumber?.() ?? undefined,
            reAmortizedFees: this.reAmortizedFees?.toNumber?.() ?? undefined,
            reAmortizedTotalPayment: this.reAmortizedTotalPayment?.toNumber?.() ?? undefined,
            reAmortizedDsiInterestDays: this.reAmortizedDsiInterestDays,
            reAmortizedPerDiem: this.reAmortizedPerDiem?.toNumber?.() ?? undefined,
            isCurrentActiveTerm: this.isCurrentActiveTerm,
            isDelinquent: this.isDelinquent,
            lastPaymentDate: this.lastPaymentDate?.toString?.() ?? undefined,
        };
        return Object.assign(base /* existing fields */);
    }
    /**
     * Recreates an AmortizationEntry from a plain JSON object
     * by letting our constructor do all the type inflation.
     */
    static fromJSON(json) {
        return new AmortizationEntry({
            term: json.term,
            // zeroPeriod: json.zeroPeriod, // or omit if auto-derived
            periodStartDate: json.periodStartDate,
            periodEndDate: json.periodEndDate,
            prebillDaysConfiguration: json.prebillDaysConfiguration,
            billDueDaysAfterPeriodEndConfiguration: json.billDueDaysAfterPeriodEndConfiguration,
            billablePeriod: json.billablePeriod,
            periodBillOpenDate: json.periodBillOpenDate,
            periodBillDueDate: json.periodBillDueDate,
            periodInterestRate: json.periodInterestRate,
            principal: json.principal,
            calendar: json.calendar,
            dueInterestForTerm: json.dueInterestForTerm,
            accruedInterestForPeriod: json.accruedInterestForPeriod,
            billedInterestForTerm: json.billedInterestForTerm,
            billedDeferredInterest: json.billedDeferredInterest,
            unbilledTotalDeferredInterest: json.unbilledTotalDeferredInterest,
            fees: json.fees,
            billedDeferredFees: json.billedDeferredFees,
            unbilledTotalDeferredFees: json.unbilledTotalDeferredFees,
            totalPayment: json.totalPayment,
            endBalance: json.endBalance,
            startBalance: json.startBalance,
            balanceModificationAmount: json.balanceModificationAmount,
            balanceModification: json.balanceModification,
            perDiem: json.perDiem,
            daysInPeriod: json.daysInPeriod,
            interestRoundingError: json.interestRoundingError,
            unbilledInterestDueToRounding: json.unbilledInterestDueToRounding,
            metadata: json.metadata,
            billingModel: json.billingModel,
            actualDSIPrincipal: json.actualDSIPrincipal,
            actualDSIInterest: json.actualDSIInterest,
            actualDSIFees: json.actualDSIFees,
            dsiInterestSavings: json.dsiInterestSavings,
            dsiInterestPenalty: json.dsiInterestPenalty,
            usageDetails: json.usageDetails,
            actualDSIStartBalance: json.actualDSIStartBalance,
            actualDSIEndBalance: json.actualDSIEndBalance,
            dsiInterestDays: json.dsiInterestDays,
            dsiPreviousPaymentDate: json.dsiPreviousPaymentDate,
            // Re-amortized fields
            reAmortizedStartBalance: json.reAmortizedStartBalance,
            reAmortizedEndBalance: json.reAmortizedEndBalance,
            reAmortizedInterest: json.reAmortizedInterest,
            reAmortizedPrincipal: json.reAmortizedPrincipal,
            reAmortizedFees: json.reAmortizedFees,
            reAmortizedTotalPayment: json.reAmortizedTotalPayment,
            reAmortizedDsiInterestDays: json.reAmortizedDsiInterestDays,
            reAmortizedPerDiem: json.reAmortizedPerDiem,
            isCurrentActiveTerm: json.isCurrentActiveTerm,
            isDelinquent: json.isDelinquent,
            lastPaymentDate: json.lastPaymentDate,
        });
    }
    get json() {
        return this.toJSON();
    }
}
